import { computed, effect, Inject, Injectable, Signal, signal } from '@angular/core';
import { CreateTRPCProxyClient, createTRPCProxyClient, httpBatchLink, httpLink } from '@trpc/client';
import type { AppRouter } from '@server/src';
import { ServerAPI } from 'kopweb-core';
import * as base64 from "byte-base64";
import { AuthService, User } from '@auth0/auth0-angular';
import { HeaderInfo } from '@wasm/KopWeb_list';
import { parse, stringify } from "devalue";
import { toSignal } from '@angular/core/rxjs-interop';
import { jwtDecode } from "jwt-decode";
import { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { HistoryEntry } from '@server/src/types';


type RouterOutput = inferRouterOutputs<AppRouter>;
type RouterInput = inferRouterInputs<AppRouter>;
type KopFormInfoType = RouterInput['klanten']['updateKlantInfo'];

@Injectable({
  providedIn: null
})
export class TrpcAPIService implements ServerAPI {

  constructor( public auth: AuthService) {
    this.currentUser = toSignal(auth.user$);
    effect(() => {
      let token = this.token();
      if (token != undefined) {
        this.readyAndAuthenticated.set(true);
      }
    }, {
      allowSignalWrites: true
    });
    effect(() => {
      let env = this.env();
      let user = this.currentUser();
      if (env != undefined && user != undefined) {
        this.initAuth();
      }
    });
    this.getEnv(); 
  }


  private env = signal<RouterOutput['getEnv'] | undefined>(undefined);
  private async getEnv() {
    let env = this.client.getEnv.query();
    this.env.set(await env);
  }
  private initAuth() {
    let env = this.env();
    if (env == undefined) {
      return;
    }
    
    this.auth.getAccessTokenSilently().subscribe(async authJwt => {
      let resp = await fetch(env.authorizationUri, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authJwt}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service: 'kopdata',
          permissions: ["read", "write", "list", "serviceadmin", "new"]
        })
      });
      let data = await resp.json();
      let retToken = data.token;
      this.token.set(retToken);
    });

  }
  
  async checkKlantPermissions(klantNo: string) {
    let payload = this.tokenPayload();
    let token = this.token();
    let env = this.env();
    if (payload == undefined || token == undefined || env == undefined) {
      return false;
    }
    if ((payload as any).systems[klantNo] == undefined) {
      let resp = await fetch(env.authorizationUri, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service: 'kopdata',
          system: klantNo,
          permissions: ["read", "write", "list", "serviceadmin"]
        })
      });
      let data = await resp.json();
      let retToken = data.token;
      this.token.set(retToken);
    }
    return true;

  }
  saveStaticTranslationRepo(repo: Record<string, Record<string, string>>): Promise<boolean> {
    return this.client.i18n.saveStaticTranslationRepo.mutate(repo);
  }
  async loadStaticTranslationRepo(lang: string): Promise<Record<string, string>> {
    let ret = await this.client.i18n.loadStaticTranslationRepo.query({ lang });
    return ret;
  }
  
  async loadKop(klantNo: string, historyId: string | undefined): Promise<{ sxbData?: Uint8Array; modulesData?: Uint8Array; }> {
    console.log("Loading Kop for klantNo", klantNo);
    await this.checkKlantPermissions(klantNo);
    let data = await this.client.file.loadKop.query({ klantNo, historyId });

    let ret: { sxbData?: Uint8Array; modulesData?: Uint8Array; } = {}
    if (data.sxbData != undefined) {
      ret.sxbData = base64.base64ToBytes(data.sxbData);
    }
    if (data.modulesData != undefined) {
      ret.modulesData = base64.base64ToBytes(data.modulesData);
    }
    return ret;
  }
  async getKopHistory(klantNo: string): Promise<HistoryEntry[]> {
    return await this.client.file.getKopHistory.query(klantNo);
  }

  async editHistory(klantNo: string, date_key: string, comment: string): Promise<boolean> {
    return await this.client.file.editHistory.mutate({ klantNo, date_key, comment });
  }

  async saveKop(klantNo: string, sxbData: Uint8Array, modulesData: Uint8Array, comment: string = "", history: boolean = true): Promise<boolean> {
    await this.checkKlantPermissions(klantNo);
    let sxbb64 = base64.bytesToBase64(sxbData);
    let modulesb64 = base64.bytesToBase64(modulesData);
    return await this.client.file.saveKop.mutate({ klantNo, sxbData: sxbb64, modulesData: modulesb64, comment: comment, history: history });
  }

  public readyAndAuthenticated = signal(false);

  public isSercom = computed(() => {
    let user = this.currentUser();
    let payload = this.tokenPayload();
    if (user != undefined && payload != undefined) {
      let isSercom = (payload as any).serviceadmin === true;
      return isSercom;
    }
    return false;
  });

  public hasLock = signal<boolean>(false, { equal: (a, b) => false });

  async canWriteKlant(klantNo: string): Promise<boolean> {
    let ret = await this.client.canWriteKlant.query(klantNo);
    return ret;
  }
  async getFileInfos(files: string[]): Promise<Record<string, Date>> {
    let ret = await this.client.file.getFileInfos.query(files);
    return ret;
  }
  async getUnlockKlantInfo(klantNo: string): Promise<HeaderInfo | undefined> {
    let ret = await this.client.klanten.getKopUnlockInfo.query(klantNo);
    return ret;
  }

  async getKlantInfo(klantNo: string) {
    let ret = await this.client.klanten.getKopInfo.query(klantNo);
    return ret;
  }

  async sendEmail(klantNo: string, bericht: string) {
    let ret = await this.client.sendEmail.mutate({ klantNo, bericht, from: this.currentUser()?.email });
    return ret;
  }

  async checkAndRenameNewKop(klantNo: string) {
    let ret = this.client.klanten.checkAndRenameNewKop.mutate(klantNo);
    return ret;
  }

  async unlock(fileName: string, info: {
    klantNo: string,
    opmerking: string,
    uren: string,
    unlockAsSercom: boolean
  }) {
    let ret = await this.client.unlock.mutate({ fileName, klantNo: info.klantNo, opmerking: info.opmerking, uren: info.uren, unlockAsSercom: info.unlockAsSercom });
    return ret;
  }
  isElectron(): boolean {
    return false;
  }
  getKlantNo(): string {
    return "ZZ0000";
  }

  private token = signal<string | undefined>(undefined);
  private tokenPayload = computed(() => {
    let token = this.token();
    if (token != undefined) {
      let tokenObj = jwtDecode(token);
      return tokenObj;
    }
    return undefined;
  });
  private currentUser: Signal<User | null | undefined>;


  privateCreateClient() {
    let d = this;

    let ret = createTRPCProxyClient<AppRouter>({
      transformer: {
        input: {
          serialize: (object) => stringify(object),
          deserialize: (object) => parse(object),
        },
        output: {
          serialize: (object) => stringify(object),
          deserialize: (object) => parse(object),
        },
      },
      links: [
        // httpBatchLink({
        //   url: window.location.origin + '/trpc',
        //   // You can pass any HTTP headers you wish here
        //   headers() {
        //     return {
        //       authorization: d.token()
        //     };
        //   }
        // })
        httpLink({
          url: window.location.origin + '/trpc',
          // You can pass any HTTP headers you wish here
          headers() {
            return {
              authorization: d.token()
            };
          }
        })
      ],
    });

    return ret;
  }

  public client: CreateTRPCProxyClient<AppRouter> = this.privateCreateClient();

  async getFile(fileName: string) {
    let b64 = await this.client.file.getFile.query(fileName);
    let b8 = base64.base64ToBytes(b64);
    return b8;
  }
  async getAsset(fileName: string) {
    let b64 = await this.client.file.getAsset.query(fileName);
    if (b64 == undefined) {
      return undefined;
    }
    let b8 = base64.base64ToBytes(b64);
    return b8;
  }
  async saveFile(fileName: string, data: Uint8Array): Promise<void> {
    let b64 = base64.bytesToBase64(data);

    await this.client.file.saveFile.mutate({ fileName, data: b64 });
  }

  async getConfigs(): Promise<any> {
    let configs = await this.client.getConfigs.query();
    return configs;
  }


}
